import { useEffect, useState } from "react";
import {
    Container,
    Grid,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
    useTheme,

} from "@mui/material";
import {
    LineChart,
    Line,
    PieChart,
    Pie,
    ResponsiveContainer,
    Cell,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
} from "recharts";


export const PaymentReport = ({ reportRange, rawData }) => {
    const theme = useTheme();

    const [chartData, setChartData] = useState([]);
    const [paymentSummary, setPaymentSummary] = useState([]);
    const [cashDropTotal, setCashDropTotal] = useState(0);
    const [cashPaidOut, setCashPaidOut] = useState(0);
    const [tipsTotal, setTipsTotal] = useState(0);

    useEffect(() => {


        if (rawData) {
            const hourlyPayments = {};
            const paymentTypeMap = {};
            let totalTips = 0;

            rawData.orders.forEach(order => {
                const hour = new Date(order.startTime).getHours();

                if (!hourlyPayments[hour]) hourlyPayments[hour] = 0;
                hourlyPayments[hour] += order.total;

                order.payment.forEach(p => {
                    const type = getPayTypeLabel(p.payType);
                    paymentTypeMap[type] = (paymentTypeMap[type] || 0) + p.amount;
                    totalTips += p.tip || 0;
                });
            });

            const chart = Object.entries(hourlyPayments).map(([hour, total]) => ({
                hour: `${hour}:00`,
                total: parseFloat(total.toFixed(2)),
            }));

            const paymentSummaryData = Object.entries(paymentTypeMap).map(([type, amount]) => ({
                type,
                amount: parseFloat(amount.toFixed(2)),
            }));

            setChartData(chart);
            setPaymentSummary(paymentSummaryData);
            setCashDropTotal(
                (rawData.dailyData.cashDrops || []).reduce((sum, c) => sum + c.amount, 0)
            );
            setCashPaidOut(
                (rawData.dailyData.paidOuts || []).reduce((sum, c) => sum + c.amount, 0)
            );
            setTipsTotal(parseFloat(totalTips.toFixed(2)));
        } else {
            setChartData([]);
            setPaymentSummary([]);
            setCashDropTotal(0);
            setCashPaidOut(0);
            setTipsTotal(0);
        }

    }, [reportRange, rawData]);
     const COLORS = [theme.palette.primary.main, theme.palette.success.main, theme.palette.warning.main, theme.palette.error.main, theme.palette.info.main];


    return (
        <Container sx={{ mt: 4, mb: 4 }}>
            {/* 1. Payment Over Time */}
            {/* <Paper elevation={3} sx={{ p: 3, mb: 3, backgroundColor: "#e8f5e9" }}>

                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <Typography variant="h6" color="success.main" gutterBottom>
                            Payments by Hour
                        </Typography>
                        <LineChart width={500} height={250} data={chartData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="hour" />
                            <YAxis />
                            <Tooltip />
                            <Legend />
                            <Line type="monotone" dataKey="total" stroke={theme.palette.success.main} />
                        </LineChart>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      // pie chart for payment summary with Type and amount
                    </Grid>

                </Grid>

            </Paper> */}
             <Paper elevation={3} sx={{ p: 3, mb: 3, backgroundColor: "#e8f5e9" }}>
                <Grid container spacing={3}>
                    {/* Line chart */}
                    <Grid item xs={12} md={6} >
                        <Typography variant="h6" color="success.main" gutterBottom>
                            Payments by Hour
                        </Typography>
                        <ResponsiveContainer width="100%" height={300}>
                            <LineChart data={chartData}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="hour" />
                                <YAxis />
                                <Tooltip />
                                <Legend />
                                <Line type="monotone" dataKey="total" stroke={theme.palette.success.main} />
                            </LineChart>
                        </ResponsiveContainer>
                    </Grid>

                    {/* Pie chart */}
                    <Grid item xs={12} md={6} sx={{ height: 300 }}>
                        <Typography variant="h6" color="primary.main" gutterBottom>
                            Payments by Type
                        </Typography>
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie
                                    data={paymentSummary}
                                    dataKey="amount"
                                    nameKey="type"
                                    cx="50%"
                                    cy="50%"
                                    outerRadius="80%"
                                    label
                                >
                                    {paymentSummary.map((entry, index) => (
                                        <Cell
                                            key={`cell-${index}`}
                                            fill={COLORS[index % COLORS.length]}
                                        />
                                    ))}
                                </Pie>
                                <Tooltip />
                                <Legend />
                            </PieChart>
                        </ResponsiveContainer>
                    </Grid>
                </Grid>
            </Paper>

            <Grid container spacing={3}>
                {/* 2. Payment Summary by Type */}
                <Grid item xs={12} md={6}>
                    <Paper elevation={2} sx={{ p: 3, backgroundColor: "#f1f8e9" }}>
                        <Typography variant="h6" color="success.dark" gutterBottom>
                            Payment Summary
                        </Typography>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Type</TableCell>
                                    <TableCell align="right">Amount ($)</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {paymentSummary.map((row) => (
                                    <TableRow key={row.type}>
                                        <TableCell>{row.type}</TableCell>
                                        <TableCell align="right">{row.amount.toFixed(2)}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Paper>
                </Grid>

                {/* 3. Summary Info */}
                <Grid item xs={12} md={6}>
                    <Paper elevation={2} sx={{ p: 3, backgroundColor: "#e3f2fd" }}>
                        <Typography variant="h6" color="info.main" gutterBottom>
                            Summary
                        </Typography>
                        <Table>
                            <TableBody>
                                <TableRow>
                                    <TableCell>Total Tips</TableCell>
                                    <TableCell align="right">${tipsTotal.toFixed(2)}</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell>Total Cash Drops</TableCell>
                                    <TableCell align="right">${cashDropTotal.toFixed(2)}</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell>Total Paid Outs</TableCell>
                                    <TableCell align="right">${cashPaidOut.toFixed(2)}</TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </Paper>
                </Grid>
            </Grid>
        </Container>
    );
};

// Utility to map payType integers to string labels
function getPayTypeLabel(type) {
    switch (type) {
        case 0: return "Cash";
        case 1: return "Gift Card";
        case 2: return "Online";
        case 3: return "Card";
        default: return "Other";
    }
}
